/**
 * S49.4 — send the user back to the page their session died on.
 *
 * ⚠️ WHY NOT `location.state`, which is the obvious answer. Re-authentication is not one hop.
 * It can run /login -> /mfa-challenge -> /select-session -> / , and router state does not
 * survive those hops; each leg would have to forward it and the OIDC/SAML callbacks re-enter
 * the app from outside the router entirely. Five landing points navigate to '/' today
 * (useAuth.ts x2, SessionSelect, MfaChallenge, OIDCCallback), so a single stashed value that
 * each of them consumes is the only version that works on every path rather than the short one.
 *
 * ⚠️ ROUTE ONLY -- NEVER FORM STATE. Persisting an in-progress certificate request or EAB
 * credential across a logout would write exactly the data the session ending is meant to stop
 * protecting, into storage that outlives the session. A path is the most that may be kept.
 *
 * sessionStorage, not localStorage: this is per-tab and dies with the tab, which matches the
 * lifetime of the intent it represents.
 */

const KEY = 'vortex-return-path';

/** Paths that must never be a destination -- returning to them would loop the user. */
const NEVER_RETURN_TO = [
  '/login',
  '/select-session',
  '/mfa-challenge',
  '/auth/callback',
  '/bootstrap',
  '/signup',
];

export function rememberReturnPath(path: string): void {
  if (!path || !path.startsWith('/')) return;
  // ⚠️ Reject protocol-relative ('//evil.com') and any absolute URL: this value is handed to
  // navigate(), and an open redirect on a login flow is worth refusing at the source even
  // though the value is same-origin today.
  if (path.startsWith('//')) return;
  if (NEVER_RETURN_TO.some((p) => path === p || path.startsWith(`${p}/`))) return;
  if (path === '/') return; // the default anyway; storing it buys nothing
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    /* storage disabled; falling back to '/' is fine */
  }
}

/** Read and clear the stashed path. Returns null when there is nothing to return to. */
export function consumeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}
